import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import Badge from '@/components/base/Badge';
import Modal from '@/components/base/Modal';
import { downloadCSV } from '@/lib/csv';
import { todayMexico, addBillingPeriod, addDays, daysBetween, nextCutDate, calcProration, formatDayMonth } from '@/lib/dates';
import { isIntegerQuantityCategory, roundQuantityForCategory } from '@/lib/productCategory';
import { type PendingAddition, runRecurringBilling } from '@/lib/recurringBilling';
import { useRecurringBilling } from '@/hooks/useRecurringBilling';

interface Suscripcion {
  id: number;
  client_id: number;
  product_id: number;
  imei: string;
  iccid: string;
  platform_id: string;
  start_date: string;
  billing_cycle: string;
  next_billing_date: string | null;
  cut_day?: number | null;
  pending_additions?: PendingAddition[];
  status: string;
  unit_price: number;
  quantity: number;
  subtotal: number;
  iva_applied: boolean;
  total_amount: number;
  client_name?: string;
  product_name?: string;
  product_sku?: string;
  product_category?: string;
}

interface ClienteOption {
  id: number;
  company_name: string;
  requires_invoice: boolean;
}

interface ProductoOption {
  id: number;
  sku: string;
  name: string;
  unit_price: number;
  category: string;
}

function calcularNextBillingDate(startDate: string, cycle: string): string | null {
  return addBillingPeriod(startDate, cycle);
}

function sumarPeriodo(fecha: string, cycle: string): string | null {
  return addBillingPeriod(fecha, cycle);
}

export default function Suscripciones() {
  const [subscriptions, setSubscriptions] = useState<Suscripcion[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstatus, setFilterEstatus] = useState('Todos');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const [showNewSubModal, setShowNewSubModal] = useState(false);
  const [showEditSubModal, setShowEditSubModal] = useState(false);
  const [editingSub, setEditingSub] = useState<Suscripcion | null>(null);
  const [clientsOpts, setClientsOpts] = useState<ClienteOption[]>([]);
  const [productsOpts, setProductsOpts] = useState<ProductoOption[]>([]);
  const [newSub, setNewSub] = useState({
    client_id: '',
    product_id: '',
    imei: '',
    iccid: '',
    platform_id: '',
    start_date: todayMexico(),
    billing_cycle: 'monthly',
    unit_price: '',
    quantity: '1',
    iva_applied: false,
  });
  const [subError, setSubError] = useState('');
  const [subSuccess, setSubSuccess] = useState('');

  const [editForm, setEditForm] = useState({
    quantity: '1',
    unit_price: '',
    status: 'active',
    billing_cycle: 'monthly',
    start_date: '',
    next_billing_date: '',
  });

  // Homologación de fecha de corte (Nueva suscripción)
  const [homologateCut, setHomologateCut] = useState(false);
  const [targetCutDate, setTargetCutDate] = useState('');
  const [clientCutDay, setClientCutDay] = useState<number | null>(null);

  // Añadir unidades a un grupo existente
  const [showAddUnitsModal, setShowAddUnitsModal] = useState(false);
  const [addUnitsSub, setAddUnitsSub] = useState<Suscripcion | null>(null);
  const [addUnitsQty, setAddUnitsQty] = useState('1');
  const [addUnitsMode, setAddUnitsMode] = useState<'immediate' | 'prorate'>('prorate');
  const [addUnitsError, setAddUnitsError] = useState('');
  const [addUnitsSuccess, setAddUnitsSuccess] = useState('');
  const [addUnitsEndDate, setAddUnitsEndDate] = useState('');
  const [addUnitsCutDate, setAddUnitsCutDate] = useState('');

  // Eliminación de suscripciones canceladas
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSub, setDeleteSub] = useState<Suscripcion | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Barrido silencioso de auto-reparación en segundo plano: al entrar a esta
  // pantalla el motor revisa las fechas de corte y genera en el fondo cualquier
  // cargo faltante del mes en curso, sin intervención manual.
  const billingResult = useRecurringBilling();

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  // Si el motor generó cargos al entrar, refresca la tabla automáticamente.
  useEffect(() => {
    if (billingResult && billingResult.created > 0) {
      fetchSubscriptions();
    }
  }, [billingResult]);

  async function fetchSubscriptions() {
    try {
      const { data: subs, error: subErr } = await supabase
        .from('erp_subscriptions')
        .select('*')
        .order('start_date', { ascending: false });

      if (subErr) throw subErr;
      if (!subs || subs.length === 0) { setSubscriptions([]); setLoading(false); return; }

      const clientIds = [...new Set(subs.map(s => s.client_id))];
      const productIds = [...new Set(subs.map(s => s.product_id))];

      const [clientsRes, prodsRes] = await Promise.all([
        supabase.from('erp_clients').select('id, company_name').in('id', clientIds),
        supabase.from('erp_products').select('id, sku, name, unit_price, category').in('id', productIds),
      ]);

      const clientMap = new Map((clientsRes.data || []).map(c => [c.id, c.company_name]));
      const prodMap = new Map((prodsRes.data || []).map(p => [p.id, p]));

      const enriched = subs.map(s => ({
        ...s,
        client_name: clientMap.get(s.client_id) || 'Cliente',
        product_name: prodMap.get(s.product_id)?.name || 'Producto',
        product_sku: prodMap.get(s.product_id)?.sku || '',
        product_category: prodMap.get(s.product_id)?.category || '',
      }));

      setSubscriptions(enriched);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadFormOptions() {
    try {
      const [cRes, pRes] = await Promise.all([
        supabase.from('erp_clients').select('id, company_name, requires_invoice').eq('status', 'active').order('company_name'),
        supabase.from('erp_products').select('id, sku, name, unit_price, category').order('category').order('name'),
      ]);
      setClientsOpts(cRes.data || []);
      setProductsOpts(pRes.data || []);
    } catch (err) {
      console.error('Error loading options:', err);
    }
  }

  // Detecta el día de corte (día del mes) más común entre las suscripciones
  // activas del cliente, para homologar una nueva unidad.
  async function detectClientCutDay(clientId: number): Promise<number | null> {
    try {
      const { data } = await supabase
        .from('erp_subscriptions')
        .select('next_billing_date')
        .eq('client_id', clientId)
        .eq('status', 'active')
        .not('next_billing_date', 'is', null);
      if (!data || data.length === 0) return null;
      const counts = new Map<number, number>();
      data.forEach((s: { next_billing_date: string }) => {
        const d = new Date(s.next_billing_date + 'T00:00:00').getDate();
        counts.set(d, (counts.get(d) || 0) + 1);
      });
      let best: number | null = null;
      let bestCount = 0;
      counts.forEach((c, d) => {
        if (c > bestCount) { bestCount = c; best = d; }
      });
      return best;
    } catch {
      return null;
    }
  }

  const filtered = useMemo(() => {
    return subscriptions.filter((s) => {
      const matchSearch = (s.client_name || '').toLowerCase().includes(search.toLowerCase())
        || (s.product_name || '').toLowerCase().includes(search.toLowerCase())
        || (s.imei || '').includes(search)
        || (s.iccid || '').includes(search)
        || (s.platform_id || '').toLowerCase().includes(search.toLowerCase());
      const matchEstatus = filterEstatus === 'Todos' || s.status === (filterEstatus === 'Activo' ? 'active' : filterEstatus === 'Suspendido' ? 'suspended' : 'cancelled');
      const matchStartDate = !filterStartDate || s.start_date >= filterStartDate;
      const matchEndDate = !filterEndDate || s.start_date <= filterEndDate;
      return matchSearch && matchEstatus && matchStartDate && matchEndDate;
    });
  }, [subscriptions, search, filterEstatus, filterStartDate, filterEndDate]);

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 6 }).format(val);

  const statusLabel = (s: string) => {
    if (s === 'active') return 'Activo';
    if (s === 'suspended') return 'Suspendido';
    return 'Cancelado';
  };

  const statusVariant = (s: string) => {
    if (s === 'active') return 'success';
    if (s === 'suspended') return 'danger';
    return 'neutral';
  };

  const cycleLabel = (c: string) => {
    if (c === 'one_time') return 'Pago Único';
    if (c === 'monthly') return 'Mensual';
    if (c === 'quarterly') return 'Trimestral';
    if (c === 'semi_annual') return 'Semestral';
    if (c === 'annual') return 'Anual';
    return c;
  };

  const subtotalCalculado = (parseFloat(newSub.unit_price) || 0) * (parseFloat(newSub.quantity) || 1);
  const ivaCalculado = newSub.iva_applied ? subtotalCalculado * 0.16 : 0;
  const totalCalculado = subtotalCalculado + ivaCalculado;
  const nextBillingPreview = calcularNextBillingDate(newSub.start_date, newSub.billing_cycle);

  const prorationDays = homologateCut && targetCutDate && newSub.start_date ? daysBetween(newSub.start_date, targetCutDate) : 0;
  const prorationPreview = calcProration(parseFloat(newSub.unit_price) || 0, prorationDays) * (parseFloat(newSub.quantity) || 1);

  const editSubtotal = (parseFloat(editForm.unit_price) || 0) * (parseFloat(editForm.quantity) || 1);
  const editIva = editingSub?.iva_applied ? editSubtotal * 0.16 : 0;
  const editTotal = editSubtotal + editIva;
  const editNextBillingPreview = calcularNextBillingDate(editForm.start_date, editForm.billing_cycle);
  const selectedProductCategory = productsOpts.find(p => String(p.id) === newSub.product_id)?.category;

  function handleExportCSV() {
    if (filtered.length === 0) return;
    const headers = ['ID', 'Cliente', 'Producto', 'SKU', 'IMEI', 'ICCID', 'Plataforma', 'Ciclo', 'Cantidad', 'Precio Unit.', 'Subtotal', 'IVA', 'Total', 'Inicio', 'Próx. Cobro', 'Estatus'];
    const rows = filtered.map(s => [
      `SUB-${String(s.id).padStart(4, '0')}`,
      s.client_name || '',
      s.product_name || '',
      s.product_sku || '',
      s.imei || '',
      s.iccid || '',
      s.platform_id || '',
      cycleLabel(s.billing_cycle),
      String(s.quantity || 1),
      String(s.unit_price),
      String(s.subtotal || 0),
      s.iva_applied ? '16%' : '0%',
      String(s.total_amount || s.subtotal || s.unit_price),
      s.start_date,
      s.next_billing_date || '—',
      statusLabel(s.status),
    ]);
    downloadCSV(headers, rows, `suscripciones-${todayMexico()}.csv`);
  }

  function resetNewSubForm() {
    setNewSub({
      client_id: '',
      product_id: '',
      imei: '',
      iccid: '',
      platform_id: '',
      start_date: todayMexico(),
      billing_cycle: 'monthly',
      unit_price: '',
      quantity: '1',
      iva_applied: false,
    });
    setHomologateCut(false);
    setTargetCutDate('');
    setClientCutDay(null);
    setSubError('');
    setSubSuccess('');
  }

  async function handleCreateSubscription() {
    setSubError('');
    setSubSuccess('');

    if (!newSub.client_id) { setSubError('Seleccione un cliente'); return; }
    if (!newSub.product_id) { setSubError('Seleccione un producto o servicio'); return; }
    if (!newSub.start_date) { setSubError('Ingrese la fecha de inicio'); return; }
    if (!newSub.unit_price || parseFloat(newSub.unit_price) <= 0) { setSubError('El precio debe ser mayor a 0'); return; }
    if (!newSub.quantity || parseFloat(newSub.quantity) < 1) { setSubError('La cantidad debe ser al menos 1'); return; }

    const qty = roundQuantityForCategory(parseFloat(newSub.quantity), productsOpts.find(p => String(p.id) === newSub.product_id)?.category);
    const price = parseFloat(newSub.unit_price);
    const subtotal = price * qty;
    const totalAmount = newSub.iva_applied ? subtotal * 1.16 : subtotal;
    const nextBilling = calcularNextBillingDate(newSub.start_date, newSub.billing_cycle);

    // Homologación de fecha de corte con prorrateo (mes comercial de 30 días).
    const homologating = homologateCut && !!targetCutDate;
    const finalNextBilling = homologating ? targetCutDate : nextBilling;
    const cutDay = homologating ? new Date(targetCutDate + 'T00:00:00').getDate() : null;
    const homologDays = homologating ? daysBetween(newSub.start_date, targetCutDate) : 0;
    const proratedCharge = homologating ? (price / 30) * Math.max(0, homologDays) * qty : 0;
    const homologIva = newSub.iva_applied ? proratedCharge * 0.16 : 0;
    const pendingAdditions: PendingAddition[] = homologating && proratedCharge > 0
      ? [{
          id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          units: qty,
          prepay_end_date: newSub.start_date,
          target_cut_date: targetCutDate,
          prorated_subtotal: proratedCharge,
          iva_amount: homologIva,
          total_amount: proratedCharge + homologIva,
          description: `Prorrateo - ${qty} Unidades (${formatDayMonth(newSub.start_date)} - ${formatDayMonth(targetCutDate)})`,
        }]
      : [];

    try {
      const { error } = await supabase
        .from('erp_subscriptions')
        .insert({
          client_id: parseInt(newSub.client_id),
          product_id: parseInt(newSub.product_id),
          imei: newSub.imei.trim() || null,
          iccid: newSub.iccid.trim() || null,
          platform_id: newSub.platform_id.trim() || null,
          start_date: newSub.start_date,
          billing_cycle: newSub.billing_cycle,
          next_billing_date: finalNextBilling,
          cut_day: cutDay,
          pending_additions: pendingAdditions,
          unit_price: price,
          quantity: qty,
          subtotal: subtotal,
          iva_applied: newSub.iva_applied,
          total_amount: totalAmount,
          status: 'active',
        });

      if (error) throw error;

      setSubSuccess('Suscripción creada exitosamente');
      await fetchSubscriptions();

      setTimeout(() => {
        setShowNewSubModal(false);
        resetNewSubForm();
      }, 800);

    } catch (err: any) {
      setSubError(err?.message || 'Error al crear la suscripción. Intente de nuevo.');
    }
  }

  function openEditModal(sub: Suscripcion) {
    setEditingSub(sub);
    setEditForm({
      quantity: String(sub.quantity || 1),
      unit_price: String(sub.unit_price || 0),
      status: sub.status,
      billing_cycle: sub.billing_cycle,
      start_date: sub.start_date,
      next_billing_date: sub.next_billing_date || '',
    });
    setSubError('');
    setSubSuccess('');
    setShowEditSubModal(true);
  }

  async function handleEditSubscription() {
    setSubError('');
    setSubSuccess('');

    if (!editingSub) return;
    if (!editForm.unit_price || parseFloat(editForm.unit_price) <= 0) { setSubError('El precio debe ser mayor a 0'); return; }
    if (!editForm.quantity || parseFloat(editForm.quantity) < 1) { setSubError('La cantidad debe ser al menos 1'); return; }

    const qty = roundQuantityForCategory(parseFloat(editForm.quantity), editingSub?.product_category);
    const price = parseFloat(editForm.unit_price);
    const subtotal = price * qty;
    const totalAmount = editingSub.iva_applied ? subtotal * 1.16 : subtotal;
    const nextBilling = calcularNextBillingDate(editForm.start_date, editForm.billing_cycle);
    const finalNextBilling = editForm.next_billing_date || nextBilling;

    try {
      const { error } = await supabase
        .from('erp_subscriptions')
        .update({
          quantity: qty,
          unit_price: price,
          subtotal: subtotal,
          total_amount: totalAmount,
          status: editForm.status,
          billing_cycle: editForm.billing_cycle,
          start_date: editForm.start_date,
          next_billing_date: finalNextBilling,
        })
        .eq('id', editingSub.id);

      if (error) throw error;

      // SINCRONIZACIÓN AUTOMÁTICA: si la nueva fecha de próximo cobro cae dentro
      // del mes en curso (o es un mes pasado aún no facturado), el motor evalúa y
      // emite el cargo en Cobranza en ese mismo instante, sin botones.
      const sync = await runRecurringBilling();

      setSubSuccess(
        sync.created > 0
          ? `Suscripción actualizada · ${sync.created === 1 ? '1 cargo generado' : `${sync.created} cargos generados`} en Cobranza`
          : 'Suscripción actualizada exitosamente',
      );
      await fetchSubscriptions();

      setTimeout(() => {
        setShowEditSubModal(false);
        setEditingSub(null);
        setSubSuccess('');
      }, 1400);

    } catch (err: any) {
      setSubError(err?.message || 'Error al actualizar la suscripción. Intente de nuevo.');
    }
  }

  async function quickToggleStatus(sub: Suscripcion, newStatus: string) {
    try {
      const { error } = await supabase
        .from('erp_subscriptions')
        .update({ status: newStatus })
        .eq('id', sub.id);

      if (error) throw error;
      await fetchSubscriptions();
    } catch (err) {
      console.error('Error toggling status:', err);
    }
  }

  function openDeleteModal(sub: Suscripcion) {
    setDeleteSub(sub);
    setDeleteError('');
    setShowDeleteModal(true);
  }

  async function handleDeleteSubscription() {
    setDeleteError('');
    if (!deleteSub) return;
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('erp_subscriptions')
        .delete()
        .eq('id', deleteSub.id);
      if (error) throw error;
      setShowDeleteModal(false);
      setDeleteSub(null);
      await fetchSubscriptions();
    } catch (err: any) {
      setDeleteError(err?.message || 'Error al eliminar la suscripción');
    } finally {
      setDeleting(false);
    }
  }

  function openAddUnits(sub: Suscripcion) {
    setAddUnitsSub(sub);
    setAddUnitsQty('1');
    setAddUnitsMode('prorate');
    setAddUnitsEndDate(todayMexico());
    const cutDay = sub.next_billing_date ? new Date(sub.next_billing_date + 'T00:00:00').getDate() : null;
    setAddUnitsCutDate(cutDay ? nextCutDate(cutDay, todayMexico()) : (sub.next_billing_date || todayMexico()));
    setAddUnitsError('');
    setAddUnitsSuccess('');
    setShowAddUnitsModal(true);
  }

  async function handleAddUnits() {
    setAddUnitsError('');
    setAddUnitsSuccess('');
    if (!addUnitsSub) return;

    const addQty = roundQuantityForCategory(parseFloat(addUnitsQty) || 0, addUnitsSub?.product_category);
    if (addQty < 1) { setAddUnitsError('Indique al menos 1 unidad a añadir'); return; }

    const price = Number(addUnitsSub.unit_price) || 0;
    const hoy = todayMexico();

    const fmtShort = (d: string) =>
      new Date(d + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });

    try {
      if (addUnitsMode === 'immediate') {
        // Cobro inmediato: se factura ya el prorrateo y la cantidad se incrementa ahora.
        const immediateCutDay = addUnitsSub.next_billing_date
          ? new Date(addUnitsSub.next_billing_date + 'T00:00:00').getDate()
          : null;
        const target = immediateCutDay ? nextCutDate(immediateCutDay, hoy) : (addUnitsSub.next_billing_date || hoy);
        const days = daysBetween(hoy, target);
        const perUnit = (price / 30) * Math.max(0, days);
        const totalProration = perUnit * addQty;
        const iva = addUnitsSub.iva_applied ? totalProration * 0.16 : 0;

        const newQty = (Number(addUnitsSub.quantity) || 1) + addQty;
        const newSubtotal = price * newQty;
        const newTotal = addUnitsSub.iva_applied ? newSubtotal * 1.16 : newSubtotal;

        const { data: client } = await supabase
          .from('erp_clients')
          .select('credit_status')
          .eq('id', addUnitsSub.client_id)
          .maybeSingle();
        const dueDate = client?.credit_status === '30days' ? addDays(hoy, 30) : hoy;
        const datePrefix = hoy.replace(/-/g, '');
        const folio = `AJUSTE-${datePrefix}-${addUnitsSub.id}`;
        const item = {
          type: 'proration',
          ref_id: addUnitsSub.id,
          name: `Unidades adicionales (${addQty}) — ${addUnitsSub.product_name || 'Servicio'}`,
          sku: addUnitsSub.product_sku || '',
          quantity: addQty,
          unit_price: perUnit,
          subtotal: totalProration,
          iva,
          total: totalProration + iva,
        };
        const { error: invErr } = await supabase.from('erp_invoices').insert({
          folio,
          client_id: addUnitsSub.client_id,
          issue_date: hoy,
          due_date: dueDate,
          total_amount: totalProration + iva,
          pending_balance: totalProration + iva,
          status: 'pending',
          items: [item],
        });
        if (invErr) throw invErr;

        const { error: updErr } = await supabase
          .from('erp_subscriptions')
          .update({ quantity: newQty, subtotal: newSubtotal, total_amount: newTotal })
          .eq('id', addUnitsSub.id);
        if (updErr) throw updErr;

        setAddUnitsSuccess(`Se añadieron ${addQty} unidad(es) y se generó una factura por ${formatCurrency(totalProration + iva)}`);
      } else {
        // Prorrateo a próxima factura: encolamos la adición en `pending_additions`.
        // La cantidad NO se incrementa de inmediato: se fusiona en la factura del
        // corte objetivo ("Expansión Alineada al Corte").
        if (!addUnitsEndDate) { setAddUnitsError('Ingrese la fecha de fin de prepago'); return; }
        if (!addUnitsCutDate) { setAddUnitsError('Ingrese la fecha de corte objetivo'); return; }

        // Motor comercial de mes de 30 días:
        //   días = diferencia exacta entre prepay_end_date y target_cut_date
        //   costo diario = precio unitario / 30
        //   subtotal prorrateado = costo diario × días × unidades añadidas
        const days = daysBetween(addUnitsEndDate, addUnitsCutDate);
        if (days < 0) { setAddUnitsError('La fecha de corte objetivo debe ser posterior al fin de prepago'); return; }

        const dailyCost = price / 30;
        const proratedSubtotal = dailyCost * days * addQty;
        const ivaAmount = addUnitsSub.iva_applied ? proratedSubtotal * 0.16 : 0;
        const totalAmount = proratedSubtotal + ivaAmount;
        const description = `Prorrateo - ${addQty} Unidades (${formatDayMonth(addUnitsEndDate)} - ${formatDayMonth(addUnitsCutDate)})`;

        const newAddition: PendingAddition = {
          id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          units: addQty,
          prepay_end_date: addUnitsEndDate,
          target_cut_date: addUnitsCutDate,
          prorated_subtotal: proratedSubtotal,
          iva_amount: ivaAmount,
          total_amount: totalAmount,
          description,
        };
        const existing = Array.isArray(addUnitsSub.pending_additions) ? addUnitsSub.pending_additions : [];

        const { error: prErr } = await supabase
          .from('erp_subscriptions')
          .update({ pending_additions: [...existing, newAddition] })
          .eq('id', addUnitsSub.id);
        if (prErr) throw prErr;

        setAddUnitsSuccess(
          `Se programaron ${addQty} unidad(es). El prorrateo de ${formatCurrency(totalAmount)} se cargará en la factura del ${fmtShort(addUnitsCutDate)}`,
        );
      }

      await fetchSubscriptions();
      setTimeout(() => setShowAddUnitsModal(false), 1800);
    } catch (err: any) {
      setAddUnitsError(err?.message || 'Error al añadir unidades');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Cargando suscripciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground-900">Suscripciones y Activos</h1>
          <p className="text-sm text-foreground-500 mt-0.5">{subscriptions.length} suscripciones registradas</p>
        </div>
        <button
          onClick={() => { resetNewSubForm(); loadFormOptions(); setShowNewSubModal(true); }}
          className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap self-start"
        >
          <i className="ri-add-line"></i>
          Nueva Suscripción
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por cliente, IMEI, ICCID, plataforma..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 placeholder:text-foreground-400"
          />
        </div>
        <input
          type="date"
          value={filterStartDate}
          onChange={(e) => setFilterStartDate(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
          title="Fecha inicio desde"
        />
        <input
          type="date"
          value={filterEndDate}
          onChange={(e) => setFilterEndDate(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
          title="Fecha inicio hasta"
        />
        <select
          value={filterEstatus}
          onChange={(e) => setFilterEstatus(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
        >
          <option value="Todos">Todos los estados</option>
          <option value="Activo">Activo</option>
          <option value="Suspendido">Suspendido</option>
          <option value="Cancelado">Cancelado</option>
        </select>
        <button
          onClick={handleExportCSV}
          className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-foreground-600 bg-white border border-background-200 rounded-lg hover:bg-background-50 cursor-pointer transition-colors whitespace-nowrap"
        >
          <i className="ri-download-2-line"></i>
          Exportar CSV
        </button>
      </div>

      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-background-100 bg-background-50">
                <th className="text-left px-4 py-3 font-semibold text-foreground-500">ID</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-500">Cliente</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-500">Producto/Servicio</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-500">IMEI</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-500">ICCID</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-500">Plataforma</th>
                <th className="text-center px-4 py-3 font-semibold text-foreground-500">Ciclo</th>
                <th className="text-center px-4 py-3 font-semibold text-foreground-500">Cant.</th>
                <th className="text-center px-4 py-3 font-semibold text-foreground-500">Próx. Cobro</th>
                <th className="text-right px-4 py-3 font-semibold text-foreground-500">Total</th>
                <th className="text-center px-4 py-3 font-semibold text-foreground-500">IVA</th>
                <th className="text-center px-4 py-3 font-semibold text-foreground-500">Estatus</th>
                <th className="text-center px-4 py-3 font-semibold text-foreground-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {filtered.map((s) => (
                <tr key={s.id} className="hover:bg-background-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="font-mono text-[10px] font-semibold text-primary-600">SUB-{String(s.id).padStart(4, '0')}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-foreground-800">{s.client_name}</span>
                      {s.status === 'suspended' && (
                        <div className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0" title="Suscripción suspendida">
                          <i className="ri-error-warning-fill text-red-500 text-[8px]"></i>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-foreground-600">{s.product_name}</span>
                    <span className="block text-[10px] text-foreground-500">{s.product_sku}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-[10px] text-foreground-600 whitespace-nowrap">{s.imei || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-[10px] text-foreground-600 whitespace-nowrap">{s.iccid || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-[10px] text-foreground-600">{s.platform_id || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={s.billing_cycle === 'one_time' ? 'warning' : 'neutral'}>{cycleLabel(s.billing_cycle)}</Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {Number(s.quantity) > 1 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-accent-100 text-accent-700">
                        {s.quantity}
                      </span>
                    ) : (
                      <span className="font-semibold text-foreground-800">{s.quantity || 1}</span>
                    )}
                    {Array.isArray(s.pending_additions) && s.pending_additions.length > 0 && (
                      <span
                        className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 whitespace-nowrap"
                        title={`${s.pending_additions.length} prorrateo(s) en cola esperando su fecha de corte`}
                      >
                        <i className="ri-time-line"></i>
                        +{s.pending_additions.reduce((acc, a) => acc + (Number(a.units) || 0), 0)} uds en cola
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {s.next_billing_date ? (
                      <div className="flex flex-col items-center">
                        <span className="font-medium text-foreground-800 whitespace-nowrap">
                          {new Date(s.next_billing_date + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                        {(() => {
                          const hoy = new Date();
                          hoy.setHours(0, 0, 0, 0);
                          const nbd = new Date(s.next_billing_date + 'T00:00:00');
                          const diff = Math.ceil((nbd.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
                          if (diff <= 7 && diff >= 0) {
                            return <span className="text-[10px] font-semibold text-amber-600">{diff === 0 ? 'Hoy' : `${diff}d`}</span>;
                          }
                          if (diff < 0) {
                            return <span className="text-[10px] font-semibold text-red-500">Vencido</span>;
                          }
                          return null;
                        })()}
                      </div>
                    ) : (
                      <span className="text-foreground-400 text-[10px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end">
                      <span className="font-semibold text-foreground-800">{formatCurrency(Number(s.total_amount) || Number(s.subtotal) || Number(s.unit_price))}</span>
                      {(s.iva_applied || (s.total_amount && Number(s.total_amount) > Number(s.subtotal || 0))) && (
                        <span className="text-[10px] text-primary-600 font-medium">IVA 16% incl.</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {s.iva_applied ? (
                      <Badge variant="info">Sí</Badge>
                    ) : (
                      <span className="text-[10px] text-foreground-400">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={statusVariant(s.status)}>{statusLabel(s.status)}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-0.5">
                      <button
                        onClick={() => openAddUnits(s)}
                        className="w-7 h-7 rounded-md bg-accent-50 hover:bg-accent-100 flex items-center justify-center cursor-pointer transition-colors"
                        title="Añadir Unidades a este Grupo"
                      >
                        <i className="ri-stack-line text-accent-600 text-xs"></i>
                      </button>
                      <button
                        onClick={() => openEditModal(s)}
                        className="w-7 h-7 rounded-md bg-amber-50 hover:bg-amber-100 flex items-center justify-center cursor-pointer transition-colors"
                        title="Editar suscripción"
                      >
                        <i className="ri-edit-line text-amber-600 text-xs"></i>
                      </button>
                      {s.status === 'active' && (
                        <>
                          <button
                            onClick={() => quickToggleStatus(s, 'suspended')}
                            className="w-7 h-7 rounded-md bg-red-50 hover:bg-red-100 flex items-center justify-center cursor-pointer transition-colors"
                            title="Suspender"
                          >
                            <i className="ri-pause-circle-line text-red-500 text-xs"></i>
                          </button>
                        </>
                      )}
                      {s.status === 'suspended' && (
                        <button
                          onClick={() => quickToggleStatus(s, 'active')}
                          className="w-7 h-7 rounded-md bg-emerald-50 hover:bg-emerald-100 flex items-center justify-center cursor-pointer transition-colors"
                          title="Reactivar"
                        >
                          <i className="ri-play-circle-line text-emerald-500 text-xs"></i>
                        </button>
                      )}
                      {s.status !== 'cancelled' && (
                        <button
                          onClick={() => quickToggleStatus(s, 'cancelled')}
                          className="w-7 h-7 rounded-md bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors"
                          title="Cancelar"
                        >
                          <i className="ri-close-circle-line text-foreground-500 text-xs"></i>
                        </button>
                      )}
                      {s.status === 'cancelled' && (
                        <button
                          onClick={() => openDeleteModal(s)}
                          className="w-7 h-7 rounded-md bg-red-50 hover:bg-red-100 flex items-center justify-center cursor-pointer transition-colors"
                          title="Eliminar suscripción"
                        >
                          <i className="ri-delete-bin-line text-red-500 text-xs"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="py-12 text-center">
            <i className="ri-refresh-line text-foreground-300 text-3xl"></i>
            <p className="text-sm text-foreground-500 mt-2">No se encontraron suscripciones</p>
          </div>
        )}
      </div>

      <Modal open={showNewSubModal} onClose={() => { setShowNewSubModal(false); resetNewSubForm(); }} title="Nueva Suscripción" size="lg">
        <div className="space-y-4">
          {subSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-emerald-500"></i>
              <p className="text-xs text-emerald-700 font-medium">{subSuccess}</p>
            </div>
          )}
          {subError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-fill text-red-500"></i>
              <p className="text-xs text-red-700 font-medium">{subError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Cliente <span className="text-red-500">*</span></label>
              <select
                value={newSub.client_id}
                onChange={(e) => {
                  const client = clientsOpts.find(c => String(c.id) === e.target.value);
                  setNewSub({
                    ...newSub,
                    client_id: e.target.value,
                    iva_applied: client?.requires_invoice ?? false,
                  });
                  if (e.target.value) {
                    detectClientCutDay(parseInt(e.target.value)).then((day) => {
                      setClientCutDay(day);
                      if (day) {
                        setHomologateCut(true);
                        setTargetCutDate(nextCutDate(day, newSub.start_date));
                      } else {
                        setHomologateCut(false);
                        setTargetCutDate('');
                      }
                    });
                  } else {
                    setClientCutDay(null);
                    setHomologateCut(false);
                    setTargetCutDate('');
                  }
                }}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
              >
                <option value="">Seleccionar cliente...</option>
                {clientsOpts.map((c) => (
                  <option key={c.id} value={c.id}>{c.company_name} {c.requires_invoice ? '(Factura)' : '(Sin factura)'}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Producto/Servicio <span className="text-red-500">*</span></label>
              <select
                value={newSub.product_id}
                onChange={(e) => {
                  const pid = e.target.value;
                  const prod = productsOpts.find(p => String(p.id) === pid);
                  setNewSub({
                    ...newSub,
                    product_id: pid,
                    unit_price: prod ? String(prod.unit_price) : newSub.unit_price,
                  });
                }}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
              >
                <option value="">Seleccionar producto...</option>
                {productsOpts.map((p) => (
                  <option key={p.id} value={p.id}>{p.sku} - {p.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Cantidad de Unidades <span className="text-red-500">*</span></label>
              <input
                type="number"
                value={newSub.quantity}
                onChange={(e) => setNewSub({ ...newSub, quantity: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="1"
                min="1"
                step={isIntegerQuantityCategory(selectedProductCategory) ? '1' : 'any'}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Precio Unitario (MXN) <span className="text-red-500">*</span></label>
              <input
                type="number"
                value={newSub.unit_price}
                onChange={(e) => setNewSub({ ...newSub, unit_price: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="0.00"
                min="0"
                step="any"
              />
              <p className="text-[10px] text-foreground-400 mt-0.5">Editable: puede modificar el precio base del catálogo</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Ciclo de Facturación</label>
              <select
                value={newSub.billing_cycle}
                onChange={(e) => setNewSub({ ...newSub, billing_cycle: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
              >
                <option value="one_time">Pago Único (Una sola vez)</option>
                <option value="monthly">Mensual</option>
                <option value="quarterly">Trimestral</option>
                <option value="semi_annual">Semestral</option>
                <option value="annual">Anual</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Fecha de Inicio <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={newSub.start_date}
                onChange={(e) => setNewSub({ ...newSub, start_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Próximo Cobro</label>
              <div className="w-full px-3 py-2 text-sm border border-background-100 rounded-lg bg-background-50 text-foreground-700">
                {nextBillingPreview ? (
                  <span>
                    {new Date(nextBillingPreview + 'T00:00:00').toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                ) : (
                  <span className="text-foreground-400">No aplica (Pago Único)</span>
                )}
              </div>
              <p className="text-[10px] text-foreground-400 mt-0.5">Calculado automáticamente según el ciclo seleccionado</p>
            </div>
          </div>

          <div className="border border-background-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-foreground-800">Homologar con fecha de corte existente del cliente</span>
                <p className="text-[10px] text-foreground-500 mt-0.5">
                  {clientCutDay ? `El cliente tiene corte el día ${clientCutDay} de cada mes` : 'Este cliente aún no tiene una fecha de corte establecida'}
                </p>
              </div>
              <button
                onClick={() => {
                  const next = !homologateCut;
                  setHomologateCut(next);
                  if (next && clientCutDay && !targetCutDate) {
                    setTargetCutDate(nextCutDate(clientCutDay, newSub.start_date));
                  }
                }}
                className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${homologateCut ? 'bg-primary-500' : 'bg-background-300'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow-sm ${homologateCut ? 'translate-x-6' : 'translate-x-0.5'}`}></span>
              </button>
            </div>

            {homologateCut && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1">Fecha de Corte Objetivo</label>
                  <input
                    type="date"
                    value={targetCutDate}
                    onChange={(e) => setTargetCutDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1">Prorrateo Calculado</label>
                  <div className="w-full px-3 py-2 text-sm border border-background-100 rounded-lg bg-background-50 text-foreground-700">
                    {formatCurrency(prorationPreview)}
                    <span className="text-[10px] text-foreground-500 block mt-0.5">{prorationDays} días · (precio/30)</span>
                  </div>
                  <p className="text-[10px] text-foreground-400 mt-0.5">Se cargará como concepto adicional en la próxima factura unificada</p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-background-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-foreground-800">Aplicar IVA 16%</span>
                <p className="text-[10px] text-foreground-500 mt-0.5">
                  {newSub.iva_applied ? 'El IVA se sumará al subtotal' : 'Sin IVA — subtotal = total'}
                </p>
              </div>
              <button
                onClick={() => setNewSub({ ...newSub, iva_applied: !newSub.iva_applied })}
                className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${newSub.iva_applied ? 'bg-primary-500' : 'bg-background-300'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow-sm ${newSub.iva_applied ? 'translate-x-6' : 'translate-x-0.5'}`}></span>
              </button>
            </div>

            <div className="border-t border-background-200 pt-3 grid grid-cols-3 gap-3 text-center">
              <div>
                <span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Subtotal</span>
                <span className="text-sm font-bold text-foreground-800">{formatCurrency(subtotalCalculado)}</span>
              </div>
              <div>
                <span className="text-[10px] text-foreground-500 uppercase tracking-wider block">IVA (16%)</span>
                <span className={`text-sm font-bold ${newSub.iva_applied ? 'text-primary-600' : 'text-foreground-400'}`}>
                  {formatCurrency(ivaCalculado)}
                </span>
              </div>
              <div>
                <span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Total</span>
                <span className="text-sm font-bold text-foreground-900">{formatCurrency(totalCalculado)}</span>
              </div>
            </div>
          </div>

          <div>
            <span className="block text-xs font-semibold text-foreground-700 mb-2">Datos Técnicos del Activo</span>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">IMEI del GPS</label>
                <input
                  type="text"
                  value={newSub.imei}
                  onChange={(e) => setNewSub({ ...newSub, imei: e.target.value.replace(/[^\d,\s]/g, '').slice(0, 300) })}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 font-mono"
                  placeholder={parseFloat(newSub.quantity) > 1 ? 'Ej. 123..., 456..., 789...' : 'Ej. 123456789012345'}
                />
                {parseFloat(newSub.quantity) > 1 && (
                  <p className="text-[10px] text-foreground-400 mt-1">Separe múltiples IMEIs con comas</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">ICCID de la SIM</label>
                <input
                  type="text"
                  value={newSub.iccid}
                  onChange={(e) => setNewSub({ ...newSub, iccid: e.target.value.replace(/[^\d,\s]/g, '').slice(0, 400) })}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 font-mono"
                  placeholder={parseFloat(newSub.quantity) > 1 ? 'Ej. 8952..., 8952..., 8952...' : 'Ej. 8952143210123456789'}
                />
                {parseFloat(newSub.quantity) > 1 && (
                  <p className="text-[10px] text-foreground-400 mt-1">Separe múltiples ICCIDs con comas</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">ID Plataforma Wialon</label>
                <input
                  type="text"
                  value={newSub.platform_id}
                  onChange={(e) => setNewSub({ ...newSub, platform_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 font-mono"
                  placeholder="Ej. WL-UNIT-12345"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowNewSubModal(false); resetNewSubForm(); }}
              className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateSubscription}
              className="px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-save-line"></i>
              Guardar Suscripción
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={showEditSubModal} onClose={() => { setShowEditSubModal(false); setEditingSub(null); setSubError(''); setSubSuccess(''); }} title="Editar Suscripción" size="md">
        <div className="space-y-4">
          {subSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-emerald-500"></i>
              <p className="text-xs text-emerald-700 font-medium">{subSuccess}</p>
            </div>
          )}
          {subError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-fill text-red-500"></i>
              <p className="text-xs text-red-700 font-medium">{subError}</p>
            </div>
          )}

          {editingSub && (
            <>
              <div className="bg-background-50 rounded-lg p-3 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-primary-600 font-semibold">SUB-{String(editingSub.id).padStart(4, '0')}</span>
                </div>
                <p className="text-sm font-semibold text-foreground-800">{editingSub.product_name}</p>
                <p className="text-xs text-foreground-500">{editingSub.client_name} · {editingSub.product_sku}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1">Cantidad <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={editForm.quantity}
                    onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                    min="1"
                    step={isIntegerQuantityCategory(editingSub?.product_category) ? '1' : 'any'}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1">Precio Unit. (MXN) <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={editForm.unit_price}
                    onChange={(e) => setEditForm({ ...editForm, unit_price: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                    min="0"
                    step="any"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1">Estatus</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
                  >
                    <option value="active">Activo</option>
                    <option value="suspended">Suspendido</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1">Ciclo de Facturación</label>
                  <select
                    value={editForm.billing_cycle}
                    onChange={(e) => setEditForm({ ...editForm, billing_cycle: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
                  >
                    <option value="one_time">Pago Único (Una sola vez)</option>
                    <option value="monthly">Mensual</option>
                    <option value="quarterly">Trimestral</option>
                    <option value="semi_annual">Semestral</option>
                    <option value="annual">Anual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1">Próximo Cobro</label>
                  <input
                    type="date"
                    value={editForm.next_billing_date}
                    onChange={(e) => setEditForm({ ...editForm, next_billing_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                  />
                  <p className="text-[10px] text-foreground-400 mt-0.5">Editable manualmente o se recalcula al cambiar ciclo</p>
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-4 grid grid-cols-3 gap-3 text-center">
                <div>
                  <span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Subtotal</span>
                  <span className="text-sm font-bold text-foreground-800">{formatCurrency(editSubtotal)}</span>
                </div>
                <div>
                  <span className="text-[10px] text-foreground-500 uppercase tracking-wider block">IVA</span>
                  <span className={`text-sm font-bold ${editingSub.iva_applied ? 'text-primary-600' : 'text-foreground-400'}`}>
                    {formatCurrency(editIva)} {!editingSub.iva_applied && '(0%)'}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Total</span>
                  <span className="text-sm font-bold text-foreground-900">{formatCurrency(editTotal)}</span>
                </div>
              </div>
              <p className="text-[10px] text-foreground-400">El IVA se determina por la configuración original de la suscripción (cliente requiere factura).</p>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowEditSubModal(false); setEditingSub(null); setSubError(''); setSubSuccess(''); }}
              className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleEditSubscription}
              className="px-4 py-2 text-xs font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-save-line"></i>
              Actualizar Suscripción
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={showAddUnitsModal} onClose={() => { setShowAddUnitsModal(false); setAddUnitsSub(null); setAddUnitsError(''); setAddUnitsSuccess(''); }} title="Añadir Unidades a este Grupo" size="md">
        <div className="space-y-4">
          {addUnitsSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-emerald-500"></i>
              <p className="text-xs text-emerald-700 font-medium">{addUnitsSuccess}</p>
            </div>
          )}
          {addUnitsError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-fill text-red-500"></i>
              <p className="text-xs text-red-700 font-medium">{addUnitsError}</p>
            </div>
          )}

          {addUnitsSub && (() => {
            const cutDay = addUnitsSub.next_billing_date ? new Date(addUnitsSub.next_billing_date + 'T00:00:00').getDate() : null;
            const defaultCut = cutDay ? nextCutDate(cutDay, todayMexico()) : (addUnitsSub.next_billing_date || todayMexico());
            const target = addUnitsMode === 'prorate' ? addUnitsCutDate : defaultCut;
            const days = addUnitsMode === 'prorate'
              ? daysBetween(addUnitsEndDate, addUnitsCutDate)
              : daysBetween(todayMexico(), defaultCut);
            const perUnit = ((Number(addUnitsSub.unit_price) || 0) / 30) * Math.max(0, days);
            const addQty = parseFloat(addUnitsQty) || 0;
            const totalPr = perUnit * addQty;
            const ivaPr = addUnitsSub.iva_applied ? totalPr * 0.16 : 0;

            return (
              <>
                <div className="bg-background-50 rounded-lg p-3 space-y-1">
                  <p className="text-sm font-semibold text-foreground-800">{addUnitsSub.product_name}</p>
                  <p className="text-xs text-foreground-500">{addUnitsSub.client_name} · {addUnitsSub.product_sku}</p>
                  <div className="flex gap-4 text-xs text-foreground-600 pt-1">
                    <span>Unidades actuales: <strong>{addUnitsSub.quantity || 1}</strong></span>
                    <span>Precio unitario: <strong>{formatCurrency(Number(addUnitsSub.unit_price) || 0)}</strong></span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1">Unidades a añadir <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={addUnitsQty}
                    onChange={(e) => { setAddUnitsQty(e.target.value); setAddUnitsError(''); }}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                    min="1"
                    step={isIntegerQuantityCategory(addUnitsSub?.product_category) ? '1' : 'any'}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-2">¿Cuándo inicia el cobro de las nuevas unidades?</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      onClick={() => setAddUnitsMode('immediate')}
                      className={`px-3 py-2.5 text-xs font-semibold rounded-lg border cursor-pointer transition-colors whitespace-nowrap ${addUnitsMode === 'immediate' ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-background-200 bg-white text-foreground-600 hover:bg-background-50'}`}
                    >
                      Cobro inmediato
                    </button>
                    <button
                      onClick={() => setAddUnitsMode('prorate')}
                      className={`px-3 py-2.5 text-xs font-semibold rounded-lg border cursor-pointer transition-colors whitespace-nowrap ${addUnitsMode === 'prorate' ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-background-200 bg-white text-foreground-600 hover:bg-background-50'}`}
                    >
                      Prorratear a próxima factura
                    </button>
                  </div>
                </div>

                {addUnitsMode === 'prorate' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-foreground-700 mb-1">Fecha de inicio de nuevo cobro / Fin de prepago</label>
                      <input
                        type="date"
                        value={addUnitsEndDate}
                        onChange={(e) => setAddUnitsEndDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground-700 mb-1">Fecha de corte objetivo</label>
                      <input
                        type="date"
                        value={addUnitsCutDate}
                        onChange={(e) => setAddUnitsCutDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                      />
                      <p className="text-[10px] text-foreground-400 mt-0.5">Siguiente ciclo: {new Date(defaultCut + 'T00:00:00').toLocaleDateString('es-MX')}</p>
                    </div>
                  </div>
                )}

                <div className="bg-background-50 rounded-xl p-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground-500">Fecha de corte</span>
                    <span className="font-medium text-foreground-800">{new Date(target + 'T00:00:00').toLocaleDateString('es-MX')}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground-500">Días prorrateados</span>
                    <span className="font-medium text-foreground-800">{days} días</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground-500">Prorrateo por unidad</span>
                    <span className="font-medium text-foreground-800">{formatCurrency(perUnit)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-background-200 pt-2">
                    <span className="font-semibold text-foreground-800">Total prorrateo</span>
                    <span className="font-bold text-foreground-900">{formatCurrency(totalPr + ivaPr)}</span>
                  </div>
                </div>
              </>
            );
          })()}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowAddUnitsModal(false); setAddUnitsSub(null); setAddUnitsError(''); setAddUnitsSuccess(''); }}
              className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleAddUnits}
              className="px-4 py-2 text-xs font-semibold text-white bg-accent-600 rounded-lg hover:bg-accent-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-stack-line"></i>
              Añadir Unidades
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={showDeleteModal} onClose={() => { if (!deleting) { setShowDeleteModal(false); setDeleteSub(null); setDeleteError(''); } }} title="Eliminar Suscripción" size="sm">
        <div className="space-y-4">
          {deleteError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-fill text-red-500"></i>
              <p className="text-xs text-red-700 font-medium">{deleteError}</p>
            </div>
          )}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-delete-bin-line text-red-500 text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-800">¿Eliminar permanentemente esta suscripción?</p>
              <p className="text-xs text-foreground-500 mt-1">
                {deleteSub ? `${deleteSub.client_name} · ${deleteSub.product_name}` : ''}
              </p>
              <p className="text-xs text-red-500 mt-1">Esta acción no se puede deshacer.</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowDeleteModal(false); setDeleteSub(null); setDeleteError(''); }}
              disabled={deleting}
              className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              onClick={handleDeleteSubscription}
              disabled={deleting}
              className="px-4 py-2 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-delete-bin-line"></i>
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}